import { Pencil, Sparkles, Compass, Home, MapPin, Wrench, DollarSign, TrendingUp, type LucideIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export interface SerializedCriteriaCard {
  label: string;
  value: string;
}

const ICON_MAP: Record<string, LucideIcon> = {
  Strategy: Compass,
  'Property Type': Home,
  Location: MapPin,
  Condition: Wrench,
  'Price Range': DollarSign,
  Profitability: TrendingUp,
};

// Co-located helpers -- callers (CrystalClearCriteriaPage) import the
// component AND the helpers from this file; splitting them costs more
// than fast-refresh recovery on dev.
// eslint-disable-next-line react-refresh/only-export-components
export const DEFAULT_CRITERIA_CARDS: SerializedCriteriaCard[] = [
  { label: 'Strategy', value: '' },
  { label: 'Property Type', value: '' },
  { label: 'Location', value: '' },
  { label: 'Condition', value: '' },
  { label: 'Price Range', value: '' },
  { label: 'Profitability', value: '' },
];

// eslint-disable-next-line react-refresh/only-export-components
export function getIconForLabel(label: string): LucideIcon {
  return ICON_MAP[label] || Compass;
}

// eslint-disable-next-line react-refresh/only-export-components
export function toDisplayCards(cards: SerializedCriteriaCard[]) {
  return cards.map(c => ({ ...c, icon: getIconForLabel(c.label) }));
}

interface CriteriaCardsDisplayProps {
  cards: SerializedCriteriaCard[];
  onEditClick: () => void;
  isSample: boolean;
  showActions?: boolean;
  secondaryAction?: 'ccc' | 'save';
  onSave?: () => void;
}

export default function CriteriaCardsDisplay({
  cards,
  onEditClick,
  isSample,
  showActions = true,
  secondaryAction = 'ccc',
  onSave,
}: CriteriaCardsDisplayProps) {
  const navigate = useNavigate();
  const displayCards = toDisplayCards(cards);

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
      {/* Criteria rows */}
      <div>
        {displayCards.map((card, i) => {
          const Icon = card.icon;
          const hasValue = Boolean(card.value);

          return (
            <div
              key={card.label}
              className={`flex items-center gap-4 px-6 py-5 ${i > 0 ? 'border-t border-border' : ''}`}
            >
              {/* Icon circle */}
              <div className="w-11 h-11 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                <Icon className="h-5 w-5 text-gold" />
              </div>

              {/* Label + value */}
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-0.5">
                  {card.label}
                </p>
                {hasValue ? (
                  <p className="font-bold text-foreground text-base leading-snug">{card.value}</p>
                ) : (
                  <p className="text-sm text-muted-foreground/60 italic">Not set</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer with action buttons */}
      {showActions && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-secondary/40">
          {secondaryAction === 'save' ? (
            <>
              <button
                onClick={onSave}
                className="px-5 py-2.5 bg-gold text-primary-foreground text-sm font-semibold rounded-lg shadow-gold hover:opacity-90 transition-opacity"
              >
                Save Changes
              </button>
              <button
                onClick={onEditClick}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Discard changes
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onEditClick}
                className="flex items-center gap-2 px-5 py-2.5 bg-gold text-primary-foreground text-sm font-semibold rounded-lg shadow-gold hover:opacity-90 transition-opacity"
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit Criteria
              </button>
              {isSample ? (
                <button
                  onClick={() => navigate('/crystal-clear-criteria')}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-gold transition-colors"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Help me choose
                </button>
              ) : (
                <button
                  onClick={() => navigate('/crystal-clear-criteria')}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Update with AI
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Clickable overlay when no footer (Phase3 mode) */}
      {!showActions && (
        <button
          onClick={onEditClick}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 border-t border-border bg-secondary/40 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
        >
          <Pencil className="h-3.5 w-3.5" />
          Click to edit your criteria
        </button>
      )}
    </div>
  );
}
